import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { UserRole, hasAnyRole } from 'types/userRoles';
import { updateTenantStatus, deleteTenant } from 'services/api/AccessManagerAPI';
import { useGetAccessManager, useGetMembers } from 'services/hooks/queries';
import { useDeleteMember, useSetCompanyOwner } from 'services/hooks/mutations';
import type { Member } from 'types/accessManager.type';
import {
    UsersSection,
    TenantsSection,
    NotificationModal,
    ConfirmationModal,
    EditUserModal,
    CreateTenantModal,
    AddUserModal,
    InviteUserModal,
} from './components';
import { memberErrorMessage } from './components/memberErrors';
import { PRODUCT_LABELS } from './products';
import { Tenant, NotificationState, ConfirmModalState } from './types';
import { Button } from 'common/components/Button';
import styles from './AccessManager.module.scss';

const AccessManagerScreen: React.FC = () => {
    // Tenants: SLAI staff only (they pick the company they manage).
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

    // Modal states
    const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState<boolean>(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState<boolean>(false);
    const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState<boolean>(false);
    const [editedMember, setEditedMember] = useState<Member | null>(null);

    // Modal states for notifications and confirmations
    const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const [notificationModal, setNotificationModal] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'success',
    });

    const { userRoles } = useAuth();
    const { accessManagerData, isError: isAccessManagerError } = useGetAccessManager();

    // Users come from user management by product: the people the caller may
    // see, with their roles in the caller's products. A super admin sees the
    // company they picked; everybody else their own.
    //
    // For a super admin nothing loads until a company is picked: without
    // account_id the answer is their own company, and every add, invite, edit
    // and delete would land there while the screen looks like it is about some
    // other company. Who is staff comes from the session's roles, not from the
    // company list: when that list fails to load, a super admin must still be
    // held back rather than mistaken for a manager of their own company.
    const isStaff = hasAnyRole(userRoles, [UserRole.SUPER_ADMIN]);
    const mustPickCompany = isStaff && !selectedTenant;
    const accountId = isStaff ? selectedTenant?.id : undefined;
    const { membersData, isMembersLoading } = useGetMembers(accountId, !mustPickCompany);
    const { deleteMember } = useDeleteMember();
    const { setCompanyOwner } = useSetCompanyOwner();
    const members = mustPickCompany ? [] : (membersData?.members ?? []);
    const manageable = mustPickCompany ? {} : (membersData?.manageable ?? {});

    useEffect(() => {
        if (!accessManagerData) return;
        if (accessManagerData.tenants?.length) {
            setTenants(accessManagerData.tenants);
        }
    }, [accessManagerData]);

    const notify = (type: 'success' | 'error', message: string): void => {
        setNotificationModal({ isOpen: true, title: type === 'success' ? 'Success' : 'Error', message, type });
    };

    // Handle tenant selection
    const handleTenantSelect = (tenant: Tenant) => {
        setSelectedTenant(tenant);
    };

    // Handle tenant deactivation
    const handleTenantDeactivation = async (tenant: Tenant): Promise<void> => {
        try {
            const isCurrentlyActive = tenant.config?.status === 'ACTIVE';
            await updateTenantStatus(tenant.id);

            const updatedTenants = tenants.map((t) => {
                if (t.id === tenant.id) {
                    return {
                        ...t,
                        config: {
                            ...t.config,
                            status: t.config?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                        },
                    };
                }
                return t;
            });

            setTenants(updatedTenants);

            setNotificationModal({
                isOpen: true,
                title: 'Success',
                message: `Tenant ${isCurrentlyActive ? 'deactivated' : 'activated'} successfully!`,
                type: 'success',
            });
        } catch (error) {
            console.error('Error updating tenant status:', error);
            setNotificationModal({
                isOpen: true,
                title: 'Error',
                message: 'Failed to update tenant status. Please try again.',
                type: 'error',
            });
        }
    };

    // Handle successful tenant creation
    const handleCreateTenantSuccess = (tenant: Tenant): void => {
        // Optimistically update local state
        setTenants((prevTenants) => [...prevTenants, tenant]);

        setNotificationModal({
            isOpen: true,
            title: 'Success',
            message: 'Tenant created successfully!',
            type: 'success',
        });
    };

    // Handle tenant creation error
    const handleCreateTenantError = (message: string): void => {
        setNotificationModal({
            isOpen: true,
            title: 'Error',
            message,
            type: 'error',
        });
    };

    // Delete: the person goes when the caller manages every product they are
    // in; otherwise they are only taken out of the caller's products.
    const handleDeleteMember = (member: Member): void => {
        setConfirmModal({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Remove ${member.first_name} ${member.last_name}? They are deleted if you manage every product they are in; otherwise they are taken out of yours.`,
            onConfirm: async () => {
                try {
                    const result = await deleteMember({ accountId, id: member.id });
                    const from = (result.removed_from ?? []).map((p) => PRODUCT_LABELS[p]).join(', ');
                    notify('success', result.deleted ? 'User deleted successfully!' : `User removed from ${from}.`);
                } catch (error) {
                    console.error('Error deleting user:', error);
                    notify('error', memberErrorMessage(error, 'Failed to delete user. Please try again.'));
                }
            },
        });
    };

    // Staff name the company's owner: a company created here has none. There
    // is one owner, so this takes it from the current one, who keeps their
    // roles.
    const handleMakeOwner = (member: Member): void => {
        if (!selectedTenant) return;
        const company = selectedTenant;
        const current = members.find((m) => m.owner);
        setConfirmModal({
            isOpen: true,
            title: 'Make Owner',
            message: current
                ? `Make ${member.first_name} ${member.last_name} the owner of ${company.name}? ${current.first_name} ${current.last_name} keeps their roles but is no longer the owner.`
                : `Make ${member.first_name} ${member.last_name} the owner of ${company.name}? The owner is an admin of every product the company has.`,
            onConfirm: async () => {
                try {
                    await setCompanyOwner({ accountId: company.id, userId: member.id });
                    notify('success', `${member.first_name} ${member.last_name} is now the owner.`);
                } catch (error) {
                    console.error('Error setting the owner:', error);
                    notify('error', memberErrorMessage(error, 'Failed to set the owner. Please try again.'));
                }
            },
        });
    };

    // Handle tenant deletion
    const handleDeleteTenant = (tenant: Tenant, e: React.MouseEvent): void => {
        e.stopPropagation();

        setConfirmModal({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete the tenant "${tenant.name}"? This action cannot be undone.`,
            onConfirm: async () => {
                try {
                    await deleteTenant(tenant.id);

                    // Optimistically update local state
                    setTenants((prevTenants) => prevTenants.filter((t) => t.id !== tenant.id));

                    if (selectedTenant && selectedTenant.id === tenant.id) {
                        setSelectedTenant(null);
                    }

                    setNotificationModal({
                        isOpen: true,
                        title: 'Success',
                        message: 'Tenant deleted successfully!',
                        type: 'success',
                    });
                } catch (error) {
                    console.error('Error deleting tenant:', error);
                    setNotificationModal({
                        isOpen: true,
                        title: 'Error',
                        message: 'Failed to delete tenant. Please try again.',
                        type: 'error',
                    });
                }
            },
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Access Manager</h1>
                {isStaff && (
                    <Button variant='primary' onClick={() => setIsCreateTenantModalOpen(true)}>
                        Create New Tenant
                    </Button>
                )}
            </div>

            <div className={`${styles.content} ${isStaff ? styles.twoColumns : styles.oneColumn}`}>
                {isStaff && (
                    <TenantsSection
                        tenants={tenants}
                        selectedTenant={selectedTenant}
                        onTenantSelect={handleTenantSelect}
                        onTenantToggleStatus={handleTenantDeactivation}
                        onTenantDelete={handleDeleteTenant}
                        emptyText={isAccessManagerError ? 'The companies could not be loaded.' : undefined}
                    />
                )}

                <div className={styles.section}>
                    <UsersSection
                        members={members}
                        manageable={manageable}
                        isLoading={isMembersLoading}
                        companyName={selectedTenant?.name}
                        onAddUser={() => setIsAddUserModalOpen(true)}
                        onInviteUser={() => setIsInviteUserModalOpen(true)}
                        onEditUser={setEditedMember}
                        onDeleteUser={handleDeleteMember}
                        onMakeOwner={isStaff && selectedTenant ? handleMakeOwner : undefined}
                        isStaff={isStaff}
                        emptyText={
                            mustPickCompany
                                ? isAccessManagerError
                                    ? 'The companies could not be loaded, so there is none to pick. Reload the page to try again.'
                                    : 'Select a company to see its users'
                                : undefined
                        }
                    />
                </div>
            </div>

            <CreateTenantModal
                isOpen={isCreateTenantModalOpen}
                onClose={() => setIsCreateTenantModalOpen(false)}
                onSuccess={handleCreateTenantSuccess}
                onError={handleCreateTenantError}
            />

            <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onSuccess={(message) => notify('success', message)}
                onError={(message) => notify('error', message)}
                accountId={accountId}
                companyName={selectedTenant?.name}
                manageable={manageable}
            />

            <InviteUserModal
                isOpen={isInviteUserModalOpen}
                onClose={() => setIsInviteUserModalOpen(false)}
                onSuccess={(message) => notify('success', message)}
                onError={(message) => notify('error', message)}
                accountId={accountId}
                companyName={selectedTenant?.name}
                manageable={manageable}
            />

            <EditUserModal
                isOpen={editedMember !== null}
                onClose={() => setEditedMember(null)}
                member={editedMember}
                onSuccess={(message) => notify('success', message)}
                onError={(message) => notify('error', message)}
                accountId={accountId}
                manageable={manageable}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            />

            <NotificationModal
                isOpen={notificationModal.isOpen}
                type={notificationModal.type}
                title={notificationModal.title}
                message={notificationModal.message}
                onClose={() => setNotificationModal({ ...notificationModal, isOpen: false })}
            />
        </div>
    );
};

// The Platform's people screen is for those who run people on the Platform:
// its MANAGER, ADMIN and staff (the sidebar offers it to the same roles).
// Someone who is only an agent here never gets to it by URL either — even when
// they run another product's people, which they do in that product.
const MANAGES_ON_THE_PLATFORM = [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN];

const AccessManager: React.FC = () => {
    const { userRoles } = useAuth();
    if (!hasAnyRole(userRoles, MANAGES_ON_THE_PLATFORM)) {
        return <Navigate to='/dashboard' replace />;
    }
    return <AccessManagerScreen />;
};

export default AccessManager;
